import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete } from "@mui/material";
import { StyledTextField } from "../StyledComponents";
import {
  getDurationDropdownOptions,
  parseCombinedDuration,
  formatCombinedDurationForDisplay,
} from "./durationUtils";

const DurationAutocompleteField = ({
  label = "Duration",
  value,
  onChange,
  durationType,
  sx,
  error: externalError,
  helperText: externalHelperText,
}) => {
  const parsed = parseCombinedDuration(value);
  const [inputValue, setInputValue] = useState(parsed.value || "");
  const [showDurationSelectionError, setShowDurationSelectionError] = useState(false);

  // Records saved before duration units were title-cased hold e.g. "2 HOURS";
  // normalize so they still match an option instead of reading as a free value.
  const displayValue = formatCombinedDurationForDisplay(value || "");

  const options = useMemo(
    () => getDurationDropdownOptions(inputValue, durationType),
    [inputValue, durationType]
  );
  const hasSelectedOption = options.includes(displayValue);
  const requireSelection =
    /^\d+$/.test(String(inputValue || "")) && !hasSelectedOption;
  const isPendingSelectionError = showDurationSelectionError && requireSelection;
  const finalError = Boolean(externalError) || isPendingSelectionError;
  const finalHelperText = externalError
    ? externalHelperText
    : isPendingSelectionError
      ? "Please select a duration"
      : "";

  useEffect(() => {
    const normalizedValue = displayValue;
    if (normalizedValue) {
      setInputValue(normalizedValue);
      return;
    }

    // Keep numeric draft while backspacing from a selected value (e.g. "8 days" -> "8").
    if (!/^\d+$/.test(String(inputValue || ""))) {
      setInputValue("");
    }
  }, [value, displayValue, inputValue]);

  return (
    <Autocomplete
      freeSolo={false}
      fullWidth
      sx={[
        { flex: 1, minWidth: 0, width: "100%" },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
      open={requireSelection && options.length > 0}
      options={options}
      value={displayValue}
      inputValue={inputValue}
      onClose={(_, reason) => {
        if (requireSelection && reason !== "selectOption") {
          return;
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "reset") return;
        if (reason === "clear" || newInputValue === "") {
          setInputValue("");
          setShowDurationSelectionError(false);
          onChange("");
          return;
        }

        if (/^\d+$/.test(newInputValue)) {
          setInputValue(newInputValue);
          setShowDurationSelectionError(true);
          onChange(newInputValue);
          return;
        }

        const numericPrefixMatch = String(newInputValue).match(/^\d+/);
        if (numericPrefixMatch) {
          // Match ChiefComplaint behavior:
          // when backspacing/typing over "8 days", keep only "8" and reopen dropdown.
          const numericPart = numericPrefixMatch[0];
          setInputValue(numericPart);
          setShowDurationSelectionError(true);
          onChange(numericPart);
          return;
        }

        const selectedParsed = parseCombinedDuration(value);
        if (selectedParsed.value) {
          setInputValue(String(selectedParsed.value));
          onChange("");
          return;
        }
      }}
      onChange={(_, selectedValue) => {
        if (!selectedValue) {
          setInputValue("");
          setShowDurationSelectionError(false);
          onChange("");
          return;
        }
        onChange(String(selectedValue));
        setInputValue(String(selectedValue));
        setShowDurationSelectionError(false);
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          fullWidth
          placeholder="Enter number"
          error={finalError}
          helperText={finalHelperText}
        />
      )}
    />
  );
};

export default DurationAutocompleteField;
